import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import PropertyCard from "../components/PropertyCard";
import {
  MapPin,
  Building2,
  TrendingUp,
  Rocket,
  Key,
  Home,
  ArrowRight,
  Clock,
} from "lucide-react";
import { Button } from "../components/ui/button";

const CityPage = () => {
  const { citySlug } = useParams();
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const cityImages = {
    bengaluru: "https://images.unsplash.com/photo-1676388347933-f9bcabb86ee8?w=1200",
    mumbai: "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=1200",
    hyderabad: "https://images.unsplash.com/photo-1708057366638-dc1f054a4019?w=1200",
    chennai: "https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=1200",
    pune: "https://images.unsplash.com/photo-1567157577867-05ccb1388e66?w=1200",
    "delhi-ncr": "https://images.unsplash.com/photo-1587474260584-136574528ed5?w=1200",
  };

  const cityDescriptions = {
    bengaluru: "India's Silicon Valley - Home to premium residential projects near major IT hubs like Whitefield, Electronic City, and Sarjapur Road.",
    mumbai: "The city of dreams - Discover luxury sea-facing apartments in Worli, Bandra, and premium developments in the suburbs.",
    hyderabad: "The City of Pearls - Find your dream home near HITEC City, Gachibowli, and the Financial District.",
    chennai: "The Gateway to South India - Explore properties on OMR IT corridor, ECR beaches, and prime city locations.",
    pune: "The Oxford of the East - Properties near Hinjewadi IT Park, Kharadi, and the vibrant city center.",
    "delhi-ncr": "The Capital Region - Premium developments in Gurugram, Noida, and Greater Noida with world-class amenities.",
  };

  const getCityName = () => {
    if (citySlug === "delhi-ncr") return "Delhi-NCR";
    return citySlug.charAt(0).toUpperCase() + citySlug.slice(1);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const cityName = getCityName();
        const [propertiesRes, statsRes] = await Promise.all([
          axios.get(`${API}/properties?city=${cityName}&limit=12`),
          axios.get(`${API}/cities/${citySlug}/stats`),
        ]);
        setProperties(propertiesRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error("Error fetching city data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [citySlug]);

  const formatPrice = (price) => {
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(1)} Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(0)} L`;
    }
    return `₹${price.toLocaleString("en-IN")}`;
  };

  const categories = [
    { value: "ready_to_move", label: "Ready to Move", icon: Key, count: stats?.ready_to_move || 0 },
    { value: "under_construction", label: "Under Construction", icon: Building2, count: stats?.under_construction || 0 },
    { value: "new_launch", label: "New Launches", icon: Rocket, count: stats?.new_launches || 0 },
    { value: "upcoming", label: "Upcoming Projects", icon: Clock, count: stats?.upcoming || 0 },
  ];

  return (
    <div className="min-h-screen" data-testid="city-page">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[400px] flex items-end">
        <div className="absolute inset-0">
          <img
            src={cityImages[citySlug] || cityImages.mumbai}
            alt={getCityName()}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/40 to-transparent" />
        </div>

        <div className="section-container relative z-10 pb-12">
          <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <span>Cities</span>
            <span>/</span>
            <span className="text-white">{getCityName()}</span>
          </div>

          <h1 className="font-display text-4xl md:text-6xl text-white mb-4">
            Properties in {getCityName()}
          </h1>
          <p className="text-white/80 max-w-2xl text-lg">
            {cityDescriptions[citySlug] || "Discover RERA-verified residential properties."}
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white border-b border-stone-100">
        <div className="section-container py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-4">
              <p className="font-mono text-3xl text-emerald-900">{stats?.total || 0}</p>
              <p className="text-xs uppercase tracking-widest text-stone-500 mt-1">Total Projects</p>
            </div>
            <div className="text-center p-4 border-l border-stone-100">
              <p className="font-mono text-3xl text-emerald-900">
                {stats?.avg_price ? formatPrice(stats.avg_price) : "—"}
              </p>
              <p className="text-xs uppercase tracking-widest text-stone-500 mt-1">Avg. Price</p>
            </div>
            <div className="text-center p-4 border-l border-stone-100">
              <p className="font-mono text-3xl text-emerald-900">{stats?.new_launches || 0}</p>
              <p className="text-xs uppercase tracking-widest text-stone-500 mt-1">New Launches</p>
            </div>
            <div className="text-center p-4 border-l border-stone-100">
              <p className="font-mono text-3xl text-emerald-900">{stats?.ready_to_move || 0}</p>
              <p className="text-xs uppercase tracking-widest text-stone-500 mt-1">Ready to Move</p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="section-spacing bg-stone-50">
        <div className="section-container">
          <h2 className="font-display text-2xl md:text-3xl text-stone-900 mb-8">Browse by Category</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {categories.map((cat) => (
              <Link
                key={cat.value}
                to={`/search?city=${getCityName()}&category=${cat.value}`}
                className="property-card p-6 hover-lift group flex items-center gap-4"
                data-testid={`city-category-${cat.value}`}
              >
                <div className="w-14 h-14 bg-emerald-50 rounded-sm flex items-center justify-center group-hover:bg-emerald-100 transition-colors flex-shrink-0">
                  <cat.icon className="w-7 h-7 text-emerald-700" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-stone-900 group-hover:text-emerald-900 transition-colors">
                    {cat.label}
                  </h3>
                  <p className="text-stone-500 text-sm">{cat.count} properties</p>
                </div>
                <ArrowRight className="w-5 h-5 text-stone-400 group-hover:text-emerald-700 group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Properties */}
      <section className="section-spacing">
        <div className="section-container">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="font-display text-2xl md:text-3xl text-stone-900">
                Featured Properties in {getCityName()}
              </h2>
              <p className="text-stone-500 mt-1">RERA-verified projects from top builders</p>
            </div>
            <Link
              to={`/search?city=${getCityName()}`}
              className="btn-secondary hidden md:flex items-center gap-2"
              data-testid="view-all-city-properties"
            >
              View All <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-stone-100 animate-pulse aspect-[4/3] rounded-sm" />
              ))}
            </div>
          ) : properties.length === 0 ? (
            <div className="bg-white p-12 text-center rounded-sm shadow-layered">
              <Home className="w-12 h-12 text-stone-300 mx-auto mb-4" strokeWidth={1} />
              <h3 className="font-display text-xl text-stone-900 mb-2">No Properties Found</h3>
              <p className="text-stone-500 mb-6">We're adding new properties every day. Check back soon!</p>
              <Link to="/search" className="btn-primary">
                Browse All Cities
              </Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {properties.map((property) => (
                  <PropertyCard key={property.property_id} property={property} />
                ))}
              </div>

              <div className="text-center mt-8 md:hidden">
                <Link to={`/search?city=${getCityName()}`} className="btn-primary inline-flex items-center gap-2">
                  View All Properties <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default CityPage;
